import { styled, css } from 'canoe';
import React, { useState } from 'react';

import { SpacingBox } from './SpacingBox';
import { ValueLink } from './ValueLink';

const Container = styled.div`
  position: relative;
  width: fit-content;
`;

const ValueField = styled.input`
  ${({ theme }) => css`
    position: absolute;
    width: 18px;
    height: 16px;
    margin-top: -1px;
    margin-bottom: 1px;
    font-size: 12px;
    line-height: 140%;
    text-align: center;
    padding-bottom: 2px;
    border: 1px solid ${theme.colors.border};
    outline: none;

    -webkit-appearance: none;

    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }
  `}
`;

const ValueLabel = styled.div<{ disabled?: boolean }>`
  ${({ disabled }) => css`
    position: absolute;
    pointer-events: none;
    width: 18px;
    height: 16px;
    text-align: center;
    padding-bottom: 0px;
    font-size: 12px;
    line-height: 100%;

    ${disabled &&
    `
      cursor: not-allowed;
      opacity: 0.2;
    `}
  `}
`;

interface ValueProps {
  isEditable?: boolean;
  disabled?: boolean;
  onChange: any;
  onKeyDown: any;
  value?: any;
  style?: any;
}

const Value = ({ disabled, isEditable, onChange, onKeyDown, value, style }: ValueProps) => {
  if (isEditable) {
    return (
      <ValueField
        type="number"
        autoFocus
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={value || ''}
        style={style}
      />
    );
  }
  return (
    <ValueLabel disabled={disabled} style={style}>
      {value || 0}
    </ValueLabel>
  );
};

type CSSValue = string | null | number;

export interface SpacingCSS {
  marginLeft: CSSValue;
  paddingLeft: CSSValue;
  marginBottom: CSSValue;
  paddingBottom: CSSValue;
  marginTop: CSSValue;
  paddingTop: CSSValue;
  marginRight: CSSValue;
  paddingRight: CSSValue;
}

interface SpacingProps {
  properties: Partial<SpacingCSS>;
  disabled?: boolean;
  noMargin?: boolean;
  setProperties: (css: Partial<SpacingCSS>) => void;
}

export const Spacing = ({
  properties,
  setProperties,
  disabled,
  noMargin = false,
}: SpacingProps) => {
  const [linkState, setLinkState] = useState({ margin: false, padding: false });
  const [editedProperty, setEditedProperty] = useState<string | null>(null);
  const {
    marginLeft,
    paddingLeft,
    marginBottom,
    paddingBottom,
    marginTop,
    paddingTop,
    marginRight,
    paddingRight,
  } = properties;

  const editProperty = (propName: string) => {
    if (editedProperty === propName) {
      setEditedProperty(null);
    } else {
      setEditedProperty(propName);
    }
  };

  const setValue = (value: any, propName: string) => {
    let valueInt: string | number = parseInt(value, 10);
    // hack: lets us set the value to 0 without considering it as a falsy value
    if (valueInt === 0) {
      valueInt = '0';
    }

    if (linkState.margin && propName.indexOf('margin') >= 0) {
      // set all margins to this value
      setProperties({
        marginTop: valueInt || null,
        marginRight: valueInt || null,
        marginBottom: valueInt || null,
        marginLeft: valueInt || null,
      });

      return;
    }

    if (linkState.padding && propName.indexOf('padding') >= 0) {
      // set all paddings to this value
      setProperties({
        paddingTop: valueInt || null,
        paddingRight: valueInt || null,
        paddingBottom: valueInt || null,
        paddingLeft: valueInt || null,
      });
      return;
    }

    setProperties({ [propName]: valueInt || null });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === 'Escape') && editedProperty !== null) {
      setEditedProperty(null);
    }
  };

  return (
    <>
      <Container>
        <SpacingBox
          linkState={linkState}
          editedProperty={editedProperty}
          editProperty={editProperty}
        />

        <ValueLink
          key="margin"
          isLinked={linkState.margin}
          disabled={disabled || noMargin}
          onClick={() => setLinkState({ ...linkState, margin: !linkState.margin })}
          style={{ top: 2, left: 24 }}
        >
          Margin
        </ValueLink>
        <ValueLink
          key="padding"
          isLinked={linkState.padding}
          disabled={disabled}
          onClick={() => setLinkState({ ...linkState, padding: !linkState.padding })}
          style={{ top: 26, left: 48 }}
        >
          Padding
        </ValueLink>

        <Value
          key="margin-left"
          style={{ top: 'calc(50% - 8px)', left: 4 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'marginLeft')
          }
          onKeyDown={onKeyDown}
          value={marginLeft}
          disabled={noMargin}
          isEditable={editedProperty === 'marginLeft' && !disabled && !noMargin}
        />
        <Value
          key="padding-left"
          style={{ top: 'calc(50% - 8px)', left: 28 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'paddingLeft')
          }
          onKeyDown={onKeyDown}
          value={paddingLeft}
          isEditable={editedProperty === 'paddingLeft' && !disabled}
        />
        <Value
          key="margin-bottom"
          style={{ bottom: 3, left: 'calc(50% - 9px)' }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'marginBottom')
          }
          onKeyDown={onKeyDown}
          value={marginBottom}
          disabled={noMargin}
          isEditable={editedProperty === 'marginBottom' && !disabled && !noMargin}
        />
        <Value
          key="padding-bottom"
          style={{ bottom: 27, left: 'calc(50% - 9px)' }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'paddingBottom')
          }
          onKeyDown={onKeyDown}
          value={paddingBottom}
          isEditable={editedProperty === 'paddingBottom' && !disabled}
        />
        <Value
          key="margin-top"
          style={{ top: 5, left: 'calc(50% - 9px)' }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'marginTop')
          }
          onKeyDown={onKeyDown}
          value={marginTop}
          disabled={noMargin}
          isEditable={editedProperty === 'marginTop' && !disabled && !noMargin}
        />
        <Value
          key="padding-top"
          style={{ top: 29, left: 'calc(50% - 9px)' }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'paddingTop')
          }
          onKeyDown={onKeyDown}
          value={paddingTop}
          isEditable={editedProperty === 'paddingTop' && !disabled}
        />
        <Value
          key="margin-left-2"
          style={{ top: 'calc(50% - 8px)', left: 4 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'marginLeft')
          }
          onKeyDown={onKeyDown}
          value={marginLeft}
          disabled={noMargin}
          isEditable={editedProperty === 'marginLeft' && !disabled && !noMargin}
        />
        <Value
          key="padding-left-2"
          style={{ top: 'calc(50% - 8px)', left: 28 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'paddingLeft')
          }
          onKeyDown={onKeyDown}
          value={paddingLeft}
          isEditable={editedProperty === 'paddingLeft' && !disabled}
        />
        <Value
          key="margin-right-2"
          style={{ top: 'calc(50% - 8px)', right: 3 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'marginRight')
          }
          onKeyDown={onKeyDown}
          value={marginRight}
          disabled={noMargin}
          isEditable={editedProperty === 'marginRight' && !disabled && !noMargin}
        />
        <Value
          key="padding-right-2"
          style={{ top: 'calc(50% - 8px)', right: 27 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value, 'paddingRight')
          }
          onKeyDown={onKeyDown}
          value={paddingRight}
          isEditable={editedProperty === 'paddingRight' && !disabled}
        />
      </Container>
    </>
  );
};
